import { ShoppingBag, Clock, MapPin, ChevronDown, AlertCircle, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { BASE_URL } from "../../../../Api/Config";

export default function OrderDetails({ userId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        const UserId = userId;

        const response = await fetch(`${BASE_URL}/orders/userOrders/${UserId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setOrders(result.data);
        } else {
          throw new Error('Failed to fetch orders');
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [userId]);

  const toggleOrderDetails = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Loading state
  if (loading) {
    return (
      <section className="py-16 px-4 bg-snowbg">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold text-redtxt flex items-center justify-center gap-2">
              <ShoppingBag className="w-8 h-8" /> Order History
            </h1>
            <div className="flex items-center justify-center gap-2 text-lightGray">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading your orders...
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="py-16 px-4 bg-snowbg">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold text-redtxt flex items-center justify-center gap-2">
              <ShoppingBag className="w-8 h-8" /> Order History
            </h1>
            <div className="flex items-center justify-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              Error loading orders: {error}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Empty state
  if (orders.length === 0) {
    return (
      <section className="py-16 px-4 bg-snowbg">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center space-y-4">
            <h1 className="text-3xl md:text-4xl font-bold text-redtxt flex items-center justify-center gap-2">
              <ShoppingBag className="w-8 h-8" /> Order History
            </h1>
            <div className="text-lightGray bg-white p-8 rounded-lg shadow-sm">
              <ShoppingBag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h2 className="text-xl font-semibold text-gray-600 mb-2">No Orders Yet</h2>
              <p>You haven't placed any orders yet. Start exploring restaurants and place your first order!</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 bg-snowbg">
      <div className="container mx-auto max-w-3xl space-y-8">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="text-center space-y-2">
            <h1 className="text-3xl md:text-4xl font-bold text-redtxt flex items-center justify-center gap-2">
              <ShoppingBag className="w-8 h-8" /> Order History
            </h1>
            <p className="text-lightGray">Review all your past food orders</p>
          </div>
        </motion.div>

        {/* Orders Timeline */}
        <div className="space-y-6">
          {orders.map((order, index) => (
            <motion.div
              key={order.id}
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="rounded-xl border bg-white shadow-sm overflow-hidden"
            >
              {/* Order Header */}
              <div className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                {/* Left side */}
                <div className="space-y-2 flex-1">
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-800">{order.restaurant}</h2>
                  <p className="text-sm text-lightGray flex items-center gap-2 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {formatDate(order.date)}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Order #:</span> {order.orderNumber}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Items:</span> {order.items.length} item{order.items.length > 1 ? 's' : ''}
                  </p>
                </div>

                {/* Right side */}
                <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-2">
                  <p className="text-lg sm:text-xl font-bold text-gray-900 order-2 sm:order-1">
                    ₹{order.totalAmount.toFixed(2)}
                  </p>
                  <motion.button
                    onClick={() => toggleOrderDetails(order.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-redtxt text-white rounded-lg hover:bg-red-700 transition-colors duration-200 text-sm font-medium order-1 sm:order-2 whitespace-nowrap"
                  >
                    {expandedOrders[order.id] ? 'Hide Details' : 'View Details'}
                    <motion.div
                      animate={{ rotate: expandedOrders[order.id] ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown size={16} />
                    </motion.div>
                  </motion.button>
                </div>
              </div>

              {/* Expanded Order Details */}
              <AnimatePresence>
                {expandedOrders[order.id] && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="border-t bg-gray-50 overflow-hidden"
                  >
                    <div className="p-4 sm:p-6 space-y-4">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-800">Order Details</h3>
                        <span className="text-sm text-lightGray break-all sm:break-normal">
                          Transaction ID: {order.transactionId}
                        </span>
                      </div>

                      {/* Items List */}
                      <div className="space-y-3">
                        {order.items.map((item, itemIndex) => (
                          <motion.div
                            key={itemIndex}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.3, delay: itemIndex * 0.1 }}
                            className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0 gap-4"
                          >
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-gray-800 block truncate">{item.name}</span>
                              <span className="text-lightGray text-sm">× {item.quantity}</span>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className="font-semibold text-gray-900">₹{item.price.toFixed(2)}</span>
                            </div>
                          </motion.div>
                        ))}
                      </div>

                      {/* Order Summary */}
                      <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.2 }}
                        className="border-t pt-4 mt-4 space-y-2"
                      >
                        <div className="flex justify-between items-center text-sm text-gray-600">
                          <span>Order Date:</span>
                          <span>{formatDate(order.date)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm text-gray-600">
                          <span>Order Number:</span>
                          <span>{order.orderNumber}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="text-lg font-bold text-gray-800">Total Amount</span>
                          <span className="text-lg font-bold text-redtxt">₹{order.totalAmount.toFixed(2)}</span>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}